import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'app-floating-dropdown',
  standalone: false,
  templateUrl: './floating-dropdown.component.html',
  styleUrl: './floating-dropdown.component.scss',
})
export class FloatingDropdownComponent implements OnChanges, OnDestroy {
  @Input() origin: CdkOverlayOrigin | null = null;
  @Input() open = false;
  @Input() align: 'start' | 'end' = 'start';
  @Input() panelClass: string | string[] = '';
  @Input() matchTriggerWidth = false;
  @Input() width: number | string | null = null;
  @Input() minWidth: number | string = '200px';
  @Input() maxHeight = 'min(24rem, calc(100vh - 1rem))';

  @Output() openChange = new EventEmitter<boolean>();

  overlayWidth: number | string = 'auto';

  private widthRefreshTimer: number | null = null;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['open'] ||
      changes['origin'] ||
      changes['matchTriggerWidth'] ||
      changes['width']
    ) {
      this.refreshOverlayWidth();
    }

    if (changes['open'] && this.open) {
      this.scheduleOverlayWidthRefresh();
    }
  }

  ngOnDestroy(): void {
    if (this.widthRefreshTimer !== null) {
      window.clearTimeout(this.widthRefreshTimer);
    }
  }

  get positions(): ConnectedPosition[] {
    const originX = this.align === 'end' ? 'end' : 'start';
    const overlayX = this.align === 'end' ? 'end' : 'start';

    return [
      { originX, originY: 'bottom', overlayX, overlayY: 'top', offsetY: 4 },
      { originX, originY: 'top', overlayX, overlayY: 'bottom', offsetY: -4 },
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
      { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    ];
  }

  private resolveWidth(): number | string {
    if (this.width !== null) {
      return this.width;
    }

    if (!this.matchTriggerWidth || !this.origin) {
      return 'auto';
    }

    return Math.ceil(this.origin.elementRef.nativeElement.getBoundingClientRect().width);
  }

  private refreshOverlayWidth(): void {
    this.overlayWidth = this.resolveWidth();
  }

  private scheduleOverlayWidthRefresh(): void {
    if (this.widthRefreshTimer !== null) {
      window.clearTimeout(this.widthRefreshTimer);
    }

    this.widthRefreshTimer = window.setTimeout(() => {
      this.widthRefreshTimer = null;
      const nextWidth = this.resolveWidth();
      if (nextWidth === this.overlayWidth) {
        return;
      }

      this.overlayWidth = nextWidth;
      this.cdr.markForCheck();
    });
  }

  close(): void {
    if (!this.open) {
      return;
    }

    this.openChange.emit(false);
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    this.close();
  }
}
